<template>
  <div
    ref="scrollRef"
    class="extra-left-legend"
    :class="{ 'fade-top': canScrollUp, 'fade-bottom': canScrollDown }"
    @scroll.passive="updateOverflowState"
  >
    <div>
      <div class="title">{{ t('bi.geomap_extraleft_title') }}</div>
      <div v-for="(item, index) in props.items" :key="index" :style="{ color: item.color }" class="mb-1">
        <div class="label">
          {{ item.label }}
        </div>
        <div class="value">
          {{ item.value.toLocaleString() }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';

//libs
import { useI18n } from 'vue-i18n';
import { useElementSize } from '@vueuse/core';

interface Items {
  label: string;
  value: number;
  color: string;
}

interface Props {
  items: Items[];
}

const props = defineProps<Props>();

const { t } = useI18n();

const scrollRef = ref<HTMLElement | null>(null);
const { height: legendHeight } = useElementSize(scrollRef);

const canScrollUp = ref(false);
const canScrollDown = ref(false);

function updateOverflowState() {
  const el = scrollRef.value;
  if (!el) return;
  canScrollUp.value = el.scrollTop > 2;
  canScrollDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
}

watch(
  [legendHeight, () => props.items],
  async () => {
    await nextTick();
    updateOverflowState();
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.extra-left-legend {
  position: absolute;
  left: 30px;
  z-index: 1;
  max-width: 160px;
  max-height: calc(100% - 20px); // การ์ดเตี้ยให้ scroll แทนทะลุออกนอกกราฟ
  overflow-y: auto;
  overflow-x: hidden;
  background: rgba(255, 255, 255, 0.85); // อ่านออกเสมอแม้ลอยทับแผนที่
  border-radius: 8px;
  padding: 8px 10px;
  scrollbar-width: none; // ใช้ fade บอกว่ามีรายการต่อแทน scrollbar

  &::-webkit-scrollbar {
    display: none;
  }

  &.fade-bottom {
    -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 24px), transparent);
    mask-image: linear-gradient(to bottom, #000 calc(100% - 24px), transparent);
  }

  &.fade-top {
    -webkit-mask-image: linear-gradient(to bottom, transparent, #000 24px);
    mask-image: linear-gradient(to bottom, transparent, #000 24px);
  }

  &.fade-top.fade-bottom {
    -webkit-mask-image: linear-gradient(to bottom, transparent, #000 24px, #000 calc(100% - 24px), transparent);
    mask-image: linear-gradient(to bottom, transparent, #000 24px, #000 calc(100% - 24px), transparent);
  }

  .title {
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  .label {
    font-size: 14px;
    color: black;
    overflow-wrap: anywhere; // ชื่อจังหวัด/พื้นที่ยาวให้พับบรรทัด ไม่โดนตัดหาย
  }

  .value {
    font-size: 25px;
    white-space: nowrap;
  }
}
</style>
